import { Plus, Edit3, Trash2, Save, Loader2 } from 'lucide-react';
import { fmtDate, fmtCislo, SECTION_COLOR } from './receptUtils';
import { MARZE_BARVY, stavMarze } from './prehledGrid';

/**
 * Příplatek ingredience (ADD) — ceník a formulář v jedné komponentě pro obě
 * hustoty (norma §1c, vzor `spizirna/Cenik.jsx`): na desktopu tabulka, na
 * mobilu blok přes celou šířku (kanál a platnost, velké číslo, meta řádek).
 * Nic se na mobilu nevypouští, jen přeskládá.
 *
 * Příplatek se zadává **s DPH** (tak ho host platí), bez DPH dopočítá server.
 * Formulář živě ukáže cenu bez DPH, náklad a marži z ceny bez DPH. Kanál funguje
 * jako u ceny produktu: bez kanálu platí všude, cena pro kanál ji přebíjí.
 */
export default function PriplatekCeny({
    ceny = [], form, setForm, saving = false, onSave, onDelete,
    naklad = null, dphSazby = [], kanalNazvy = {},
    isMobile = false, isDark = false, mutedColor, secondaryColor,
    inputStyle, selectStyle, optionStyle, btnPrimary, btnSecondary, thStyle, tdStyle, renderFormField,
}) {
    const jemna = isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)';
    const cil = isMobile ? 44 : 28;

    // Marže z ceny bez DPH proti nákladu příplatku (balení příplatek nemá).
    const marzeZ = (bezDph) => {
        if (naklad == null || !(bezDph > 0)) return null;
        const marze = bezDph - naklad;
        return { marze, pct: (marze / bezDph) * 100 };
    };

    const kanalCip = (kanal) => (kanal ? (
        <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold" style={{ background: 'rgba(16,185,129,0.14)', color: '#10B981' }}>
            {kanalNazvy[kanal] || kanal}
        </span>
    ) : (
        <span className="text-[11px]" style={{ color: mutedColor }}>všechny kanály</span>
    ));

    const upravit = (c) => setForm({
        ...c, kanal: c.kanal || null,
        cena_s_dph: String(parseFloat(c.cena_s_dph)),
        platnost_od: c.platnost_od?.split('T')[0], platnost_do: c.platnost_do?.split('T')[0] || '',
    });

    const akceTlacitko = (Ikona, popis, onClick, barva) => (
        <button onClick={onClick} title={popis} aria-label={popis}
            className="flex items-center justify-center rounded-md"
            style={{ width: cil, height: cil, background: 'none', border: 'none', cursor: 'pointer', color: barva, flexShrink: 0, WebkitTapHighlightColor: 'transparent' }}>
            <Ikona size={isMobile ? 16 : 13} />
        </button>
    );

    const marzeText = (bezDph) => {
        const m = marzeZ(bezDph);
        if (!m) return null;
        const barva = MARZE_BARVY[stavMarze(m.pct)];
        return <span style={{ color: barva || undefined, fontWeight: barva ? 600 : 400 }}>marže {Math.round(m.pct)} %</span>;
    };

    const seznam = ceny.length > 0 && (isMobile ? (
        // Mobil: blok na cenu — kdo/kdy, velké číslo, zbytek jako meta řádek.
        <div className="mb-3" style={{ borderTop: `1px solid ${jemna}` }}>
            {ceny.map(c => (
                <div key={c.id} style={{ borderBottom: `1px solid ${jemna}`, padding: '8px 0' }}>
                    <div className="flex items-center gap-2">
                        {kanalCip(c.kanal)}
                        <span className="text-[11px] flex-1 min-w-0 truncate" style={{ color: mutedColor }}>
                            od {fmtDate(c.platnost_od)}{c.platnost_do ? ` do ${fmtDate(c.platnost_do)}` : ''}
                        </span>
                        {akceTlacitko(Edit3, 'Upravit příplatek', () => upravit(c), secondaryColor)}
                        {akceTlacitko(Trash2, 'Smazat příplatek', () => onDelete(c), '#EF4444')}
                    </div>
                    <div className="flex items-baseline gap-2">
                        <span style={{ fontSize: 21, fontWeight: 700, color: 'var(--text-primary)', lineHeight: 1.1 }}>{fmtCislo(c.cena_s_dph)}</span>
                        <span className="text-xs" style={{ color: secondaryColor }}>Kč s DPH</span>
                    </div>
                    <div className="text-[11px] mt-1" style={{ color: mutedColor }}>
                        bez DPH {fmtCislo(c.cena_bez_dph)} Kč · DPH {c.dph_sazba} %
                        {marzeZ(parseFloat(c.cena_bez_dph)) && <> · {marzeText(parseFloat(c.cena_bez_dph))}</>}
                        {c.poznamka ? ` · ${c.poznamka}` : ''}
                    </div>
                </div>
            ))}
        </div>
    ) : (
        <div className="rounded-xl overflow-hidden mb-3" style={{ border: `1px solid ${jemna}` }}>
            <table className="w-full">
                <thead><tr style={{ borderBottom: `1px solid ${jemna}` }}>
                    <th style={thStyle}>Kanál</th><th style={thStyle}>Příplatek s DPH</th><th style={thStyle}>DPH</th>
                    <th style={thStyle}>Bez DPH</th><th style={thStyle}>Marže</th><th style={thStyle}>Od</th><th style={thStyle}>Do</th><th style={thStyle}></th>
                </tr></thead>
                <tbody>{ceny.map(c => (
                    <tr key={c.id} style={{ borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.04)' : 'var(--border-default)'}` }}>
                        <td style={tdStyle}>{kanalCip(c.kanal)}</td>
                        <td style={{ ...tdStyle, fontWeight: 600, color: SECTION_COLOR }}>{parseFloat(c.cena_s_dph).toFixed(2)} Kč</td>
                        <td style={tdStyle}>{c.dph_sazba}%</td>
                        <td style={tdStyle}>{parseFloat(c.cena_bez_dph).toFixed(2)} Kč</td>
                        <td style={tdStyle}>{marzeText(parseFloat(c.cena_bez_dph)) || '—'}</td>
                        <td style={tdStyle}>{fmtDate(c.platnost_od)}</td>
                        <td style={tdStyle}>{c.platnost_do ? fmtDate(c.platnost_do) : '—'}</td>
                        <td style={tdStyle}>
                            <div className="flex gap-1">
                                {akceTlacitko(Edit3, 'Upravit příplatek', () => upravit(c), secondaryColor)}
                                {akceTlacitko(Trash2, 'Smazat příplatek', () => onDelete(c), '#EF4444')}
                            </div>
                        </td>
                    </tr>
                ))}</tbody>
            </table>
        </div>
    ));

    const tlacitkoPlne = isMobile ? { width: '100%', justifyContent: 'center', minHeight: 44 } : null;

    // Živě: bez DPH a marže z ceny bez DPH proti nákladu příplatku.
    const napoveda = (() => {
        if (!form) return null;
        const sDph = parseFloat(form.cena_s_dph);
        if (isNaN(sDph)) return null;
        const bezDph = sDph / (1 + (form.dph_sazba || 0) / 100);
        const m = marzeZ(bezDph);
        return (
            <>
                Bez DPH {bezDph.toFixed(2)} Kč.
                {m && (
                    <> Náklad {naklad.toFixed(2)} Kč · marže{' '}
                        <b style={{ color: MARZE_BARVY[stavMarze(m.pct)] || 'var(--text-primary)' }}>
                            {m.marze.toFixed(2)} Kč ({m.pct.toFixed(0)} %)
                        </b>{' '}z ceny bez DPH.</>
                )}{' '}
            </>
        );
    })();

    return (
        <div>
            {seznam}
            {!form && (
                <button onClick={() => setForm({
                    kanal: null, cena_s_dph: '',
                    dph_sazba: ceny[0]?.dph_sazba != null ? parseInt(ceny[0].dph_sazba) : 21,
                    platnost_od: new Date().toISOString().split('T')[0], platnost_do: '',
                })} style={{ ...btnPrimary, ...tlacitkoPlne }}>
                    <Plus size={14} /> Přidat příplatek
                </button>
            )}
            {form && (
                // Na mobilu bez rámečku (karta v kartě ubírá šířku) — jen linka nahoře.
                <div className={`space-y-3 mt-2 ${isMobile ? 'pt-3' : 'rounded-xl p-4'}`}
                    style={isMobile ? { borderTop: `1px solid ${jemna}` } : { border: `1px solid ${jemna}` }}>
                    <div className={`grid gap-3 ${isMobile ? 'grid-cols-1' : 'grid-cols-4'}`}>
                        {renderFormField('Kanál', <select value={form.kanal || ''} onChange={e => setForm({ ...form, kanal: e.target.value || null })} style={selectStyle}>
                            <option value="" style={optionStyle}>Všechny kanály</option>
                            {Object.entries(kanalNazvy).map(([k, v]) => <option key={k} value={k} style={optionStyle}>{v}</option>)}
                        </select>)}
                        {renderFormField('Příplatek s DPH *', <input type="number" step="0.01" inputMode="decimal" value={form.cena_s_dph ?? ''} onChange={e => setForm({ ...form, cena_s_dph: e.target.value })} style={inputStyle} />)}
                        {renderFormField('DPH', <select value={form.dph_sazba} onChange={e => setForm({ ...form, dph_sazba: parseInt(e.target.value) })} style={selectStyle}>
                            {dphSazby.map(d => <option key={d.sazba} value={d.sazba} style={optionStyle}>{d.nazev}</option>)}
                        </select>)}
                        {renderFormField('Poznámka', <input value={form.poznamka || ''} onChange={e => setForm({ ...form, poznamka: e.target.value })} style={inputStyle} />)}
                    </div>
                    <div className={`grid gap-3 ${isMobile ? 'grid-cols-1' : 'grid-cols-2'}`}>
                        {renderFormField('Platnost od *', <input type="date" value={form.platnost_od || ''} onChange={e => setForm({ ...form, platnost_od: e.target.value })} style={inputStyle} />)}
                        {renderFormField('Platnost do', <input type="date" value={form.platnost_do || ''} onChange={e => setForm({ ...form, platnost_do: e.target.value || null })} style={inputStyle} />)}
                    </div>
                    <p className="text-[11px]" style={{ color: mutedColor }}>
                        {napoveda}
                        Příplatek bez kanálu platí všude, příplatek pro kanál ho přebíjí.
                    </p>
                    <div className={isMobile ? 'flex flex-col-reverse gap-2' : 'flex justify-end gap-2'}>
                        <button onClick={() => setForm(null)} style={{ ...btnSecondary, ...tlacitkoPlne }}>Zrušit</button>
                        <button onClick={onSave} style={{ ...btnPrimary, ...tlacitkoPlne }} disabled={saving}>
                            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Uložit
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
